"use client";
import { useEffect, useRef, useState } from "react";
import { Link, useMatch, useNavigate } from "react-router-dom";
import { AnimatePresence } from "framer-motion";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { FiArrowRight, FiChevronDown, FiLock, FiX } from "react-icons/fi";
import data from "../../JsonFolders/portfolio.json"
import { projectSlug } from "../../data/profile";
import { projectMeta } from "../../data/pageMeta";
import { applyPageMeta } from "../../usePageMeta";
import SectionHeading from "../SectionHeading/SectionHeading";
import ProjectDetail from "./ProjectDetail";
import TechStack from "./TechStack";
import FeaturedCases from "./FeaturedCases";
import './Project.css'

function ProjectCard({ proj, techFilter }) {
  const isPrivate = Boolean(proj.linkNote);
  // The on-site chat project uses an icon rather than a screenshot, so don't crop it
  const isIcon = !isPrivate && (!proj.link || proj.link === "#");

  return (
    <article className="project-card">
      <Link to={`/projects/${proj.slug}`} className="project-link">
        <div className="project-media">
          <img
            className={`project-image ${isIcon ? "project-image-icon" : ""}`}
            src={proj.src}
            alt={proj.alt}
            loading="lazy"
          />
        </div>

        <div className="project-body">
          <h3 className="project-title">{proj.title}</h3>
          {proj.highlight && <p className="project-highlight">{proj.highlight}</p>}
          <p className="project-text">{proj.description}</p>
          <span className="project-footer">
            <span className="project-cta">
              Read more <FiArrowRight className="icon-right" aria-hidden="true" />
            </span>
            {isPrivate && (
              <span className="project-cta-muted">
                <FiLock aria-hidden="true" /> {proj.linkNote}
              </span>
            )}
          </span>
          <TechStack stack={proj.stack} active={techFilter} />
        </div>
      </Link>
    </article>
  );
}

const PROJECTS = data.images.projects.map((p) => ({ ...p, slug: projectSlug(p.title) }));
const featured = PROJECTS.slice(0, 2);
const remaining = PROJECTS.slice(2);
const visible = remaining.slice(0, 3);
const more = remaining.slice(3);
const ORDERED = [...featured, ...remaining];

const ProjectsSection = ({ techFilter, onTechFilter }) => {
  const navigate = useNavigate();
  const match = useMatch("/projects/:slug");
  const certificateMatch = useMatch("/certificates/:slug");
  const index = match ? ORDERED.findIndex((p) => p.slug === match.params.slug) : -1;
  const selected = index >= 0 ? ORDERED[index] : null;

  const [showMore, setShowMore] = useState(false);
  const sectionRef = useRef(null);

  useEffect(() => {
    const frame = requestAnimationFrame(() => ScrollTrigger.refresh());
    return () => cancelAnimationFrame(frame);
  }, [showMore, techFilter]);

  // Arriving on a project link (or asking the chat for one) that lives under "more":
  // expand it so the dialog closes back onto its card
  useEffect(() => {
    if (selected && more.some((p) => p.slug === selected.slug)) setShowMore(true);
  }, [selected]);

  // Landing directly on /projects/...: park the page on the projects section behind the dialog
  useEffect(() => {
    if (match) sectionRef.current?.scrollIntoView({ block: "start" });
    // Only on first load
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Each write-up gets its own title, description and canonical URL, so shared links,
  // history and search results read properly
  useEffect(() => {
    if (!certificateMatch) applyPageMeta(selected ? projectMeta(selected, selected.slug) : undefined);
  }, [selected, certificateMatch]);

  // Unknown project slug: fall back to the home page
  useEffect(() => {
    if (match && !selected) navigate("/", { replace: true });
  }, [match, selected, navigate]);

  const close = () => {
    // Replace rather than push, so the browser's back button doesn't reopen the dialog
    navigate("/", { replace: true });
  };

  // With a technology picked, every matching project shows in one grid, featured or not
  const filtered = techFilter ? ORDERED.filter((p) => p.stack?.includes(techFilter)) : null;

  // Cards ease in a beat apart, left to right along each row
  const card = (proj, i) => (
    <div key={proj.title} className="col-12 col-sm-6 col-lg-4 d-flex" data-reveal style={{ "--i": i % 3 }}>
      <ProjectCard
        proj={proj}
        techFilter={techFilter}
      />
    </div>
  );

  const toggle = () => {
    // When collapsing, bring the section back into view so the page doesn't jump past it
    if (showMore) sectionRef.current?.scrollIntoView({ block: "start" });
    setShowMore((v) => !v);
  };

  return (
    <div id="projects">
      <section ref={sectionRef} className="section projects" aria-labelledby="projects-title">
        <div className="container">
          <SectionHeading
            title="Projects"
            id="projects-title"
            intro="Two larger systems first, written up as case studies. Smaller apps and experiments follow."
          />
          {!techFilter && <FeaturedCases />}
          <div className={`more-work-heading ${techFilter ? "is-flush" : ""}`} data-reveal>
            <h3>{techFilter ? `Built with ${techFilter}` : "Other projects"}</h3>
            {!techFilter && <p className="tabular">{remaining.length} more</p>}
          </div>

          {filtered ? (
            <>
              <div className="projects-filter" role="status">
                <span>
                  Showing <strong>{filtered.length}</strong> project{filtered.length === 1 ? "" : "s"} built with{" "}
                  <strong>{techFilter}</strong>
                </span>
                <button type="button" className="projects-filter-clear" onClick={() => onTechFilter(null)}>
                  <FiX aria-hidden="true" /> Show all projects
                </button>
              </div>

              <div key={techFilter} className="row g-4">
                {filtered.map(card)}
              </div>
            </>
          ) : (
            <>
              <div className="row g-4">{visible.map(card)}</div>

              {showMore && <div id="more-projects" className="row g-4 mt-0">{more.map(card)}</div>}

              {more.length > 0 && (
                <div className="projects-more">
                  <button
                    type="button"
                    className="btn-brand-outline"
                    onClick={toggle}
                    aria-expanded={showMore}
                    aria-controls="more-projects"
                  >
                    {showMore ? "Show fewer projects" : `Show ${more.length} more`}
                    <FiChevronDown className={`projects-more-chevron ${showMore ? "is-open" : ""}`} aria-hidden="true" />
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </section>

      <AnimatePresence>
        {selected && (
          <ProjectDetail
            key="project-detail"
            proj={selected}
            slug={selected.slug}
            prev={ORDERED[index - 1]}
            next={ORDERED[index + 1]}
            sharedMedia={false}
            onClose={close}
            onGo={(slug) => navigate(`/projects/${slug}`, { replace: true })}
          />
        )}
      </AnimatePresence>
    </div>
  );
};

export default ProjectsSection;
